import type { MapDefinition, Point } from './world-types.js';
import { terrainLineOfSight } from './cliffs.js';
import { levelMap, meetingLevel } from './caves.js';
import { clearLineOfSight, type Rectangle } from '../navigation/geometry.js';

// Scenery blocks sight top-down; occupied bases constrain movement without blocking sight.
// Cliffs are the exception: they hide only from below (`terrainLineOfSight`).
/** The battlefield: a map, or bare obstacles on flat ground. */
export type Battleground = MapDefinition | Rectangle[];

/** On a map with caves, the ground two units share (`meetingLevel`), or null when rock lies between them. */
export function sharedGround(a: Point & { level?: number }, b: Point & { level?: number }, ground: Battleground): Battleground | null {
  if (Array.isArray(ground) || !ground.caves?.length) return ground;
  const level = meetingLevel(ground, a, b);
  return level === undefined ? null : levelMap(ground, level);
}

/** Whether `viewer` sees `target`: the combat line of sight the server rules on and the browser's hiding overlay draws. */
export function hasLineOfSight(viewer: Point & { level?: number }, target: Point & { level?: number }, ground: Battleground): boolean {
  const on = sharedGround(viewer, target, ground);
  if (!on) return false;
  return Array.isArray(on) ? clearLineOfSight(on, viewer, target) : terrainLineOfSight(on, viewer, target);
}

/** A foe in a fight sees ahead in a cone this wide (radians) and this deep (metres, centre to centre), and all round within `NEAR_SENSE`. */
export const SIGHT_CONE = Math.PI * 2 / 3, SIGHT_RANGE = 20, NEAR_SENSE = 3.5;

/**
 * Which way a watcher looks: at a noise it glances toward (`lookAt`), else along its last step while it walks, at its target while it stands (or has never stepped) and has one, as its
 * figure turns, otherwise the way it last faced; with none of these it sees all round. Shared so the hiding overlay draws the cones the server judges by.
 */
export function viewHeading(viewer: Point & { heading?: number; route?: readonly Point[]; lookAt?: Point }, target?: Point): number | undefined {
  if (viewer.lookAt && Math.hypot(viewer.lookAt.x - viewer.x, viewer.lookAt.z - viewer.z) > .01) return Math.atan2(viewer.lookAt.x - viewer.x, viewer.lookAt.z - viewer.z);
  if ((!viewer.route?.length || viewer.heading === undefined) && target && Math.hypot(target.x - viewer.x, target.z - viewer.z) > .01) return Math.atan2(target.x - viewer.x, target.z - viewer.z);
  return viewer.heading;
}

/** Whether `point` lies in a watcher's view (its near circle, or its cone): an adaptation of Full Cover, so a hero can slip round a foe's back. */
export function inView(viewer: Point, heading: number | undefined, point: Point): boolean {
  const dx = point.x - viewer.x, dz = point.z - viewer.z, distance = Math.hypot(dx, dz);
  if (distance <= NEAR_SENSE) return true;
  if (distance > SIGHT_RANGE || heading === undefined) return heading === undefined && distance <= SIGHT_RANGE;
  const off = Math.atan2(dx, dz) - heading;
  return Math.abs(Math.atan2(Math.sin(off), Math.cos(off))) <= SIGHT_CONE / 2;
}

/** A watcher sees a point when it is in view and nothing blocks the line. */
export function sees(viewer: Point & { level?: number }, heading: number | undefined, point: Point & { level?: number }, ground: Battleground): boolean {
  return inView(viewer, heading, point) && hasLineOfSight(viewer, point, ground);
}
